import {
  Routes,
  Route,
  BrowserRouter,
  HashRouter,
  useNavigate,
} from "react-router-dom";
import PrivateRoute from "./routes/PrivateRoute";
import { useAppDispatch } from "./store/hooks";
import { Suspense, useEffect } from "react";
import { fetchStoreLocations, fetchStoreMenu } from "./store/storeSlice";
import { useCart, useStore } from "./hooks";
import { privateRoutes, publicRoutes } from "./routes";
import RouteProgressBar from "./components/loaders/RouteProgressBar";
import RouteFallback from "./components/loaders/RouteFallback";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";
import { useRoutePrefetch } from "./routes/prefetch";
import { setNavigate } from "./services/NavigationService";
import { Error404 } from "./pages/Error404";
import { getEmbedConfig } from "./embed/config";
import { FrameSync } from "./components/FrameSync";

const NavigationInitializer = () => {
  const navigate = useNavigate();
  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);
  return null;
};

/**
 * Warms route chunks and drives the navigation progress bar. Must sit inside
 * the router — it reads the committed location to know when a navigation has
 * actually landed.
 */
const RoutePrefetcher = () => {
  useRoutePrefetch();
  return null;
};

const CART_EXPIRY = 2 * 60 * 60 * 1000;

/**
 * Router selection.
 *
 * Standalone keeps path routing under /olo/{storeId}, exactly as before.
 * Embedded, hash routing is the default: a client domain would otherwise need a
 * catch-all rewrite so that /online-ordering/checkout serves the same page, and
 * requiring server config from the client is the thing we are trying to avoid.
 * Clients willing to add that rewrite can opt into path mode.
 */
const { routerMode, basename } = getEmbedConfig();
const Router = routerMode === "hash" ? HashRouter : BrowserRouter;

export const App: React.FC = () => {
  const { storeId } = useStore();
  const { uusid, setUusid, setCartItems } = useCart();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!storeId) return;

    dispatch(fetchStoreMenu(storeId));
    dispatch(fetchStoreLocations(storeId));

    const now = Date.now();

    if (!uusid || now - uusid > CART_EXPIRY) {
      setCartItems([]);
      setUusid(now);
    }
  }, [storeId]);

  return (
    <Router basename={basename || undefined}>
      <NavigationInitializer />
      {/* Mirrors our route to the host page when running inside an iframe, and
          accepts deep links / back-forward from it. No-op when not framed. */}
      <FrameSync />
      <RoutePrefetcher />
      <RouteProgressBar />
      <ChunkErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {publicRoutes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            ))}

            <Route element={<PrivateRoute />}>
              {privateRoutes.map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={route.element}
                />
              ))}
            </Route>

            <Route path="/*" element={<Error404 />} />
          </Routes>
        </Suspense>
      </ChunkErrorBoundary>
    </Router>
  );
};
